/* eslint-disable camelcase */
type QueuedCall = () => void;

export interface JarabotUserIdentity {
	name?: string;
	email?: string;
	// eslint-disable-next-line camelcase
	phone_number?: string;
	// eslint-disable-next-line camelcase
	country_code?: string;
}

export interface JarabotContactAttributes {
	'contract.type'?: string;
	'contract.activeProducts'?: string[];
	'contract.availableAmountState'?: string;
	'contract.customerBlockingReasons'?: string[];
	kyc_verified?: boolean;
	'customer.amlLegalReview.isRequested'?: boolean;
}

export const JARABOT_EVENTS = {
	CUSTOMER_STUCK_ON_SMS: 'customer_stuck_on_sms_in_gate',
	CUSTOMER_STUCK_ON_SMS_HANDOFF: 'customer_stuck_on_sms_in_gate_handoff',
	CUSTOMER_STUCK_ON_IDENTITY_VERIFICATION: 'customer_stuck_on_identity_verification',
	CUSTOMER_INTERRUPTED_IDENTITY_VERIFICATION: 'customer_interrupted_identity_verification',
	CUSTOMER_STUCK_ON_CREDIT_APPLICATION_START: 'customer_stuck_on_credit_application_start',
	CUSTOMER_STUCK_ON_REVOLVING_SUMMARY_BEFORE_PERSONAL_DATA:
		'customer_stuck_on_revolving_summary_before_personal_data',
	CUSTOMER_KYC_NAME_MISMATCH_CANCELLATION: 'customer_KYC_name_mismatch_cancellation_in_gate',
} as const;

export type JarabotEvent = (typeof JARABOT_EVENTS)[keyof typeof JARABOT_EVENTS];

export const JARABOT_ACTIONS = {
	CONTINUE_APPLICATION: 'continue_application',
} as const;

export const JARABOT_STORAGE_KEYS = {
	SMS_DROPOFF_ENABLED: '__jarabot_sms_dropoff_enabled',
	SMS_TRIGGER_1_DELAY: '__jarabot_trigger1_delay',
	SMS_TRIGGER_2_DELAY: '__jarabot_trigger2_delay',
	IDENTITY_DROPOFF_ENABLED: '__jarabot_identity_dropoff_enabled',
	IDENTITY_DELAY: '__jarabot_identity_delay',
	IDENTITY_SIMULATE_BANKID_STARTED: '__jarabot_identity_simulate_bankid_started',
	CREDIT_APP_START_ENABLED: '__jarabot_credit_app_start_enabled',
	CREDIT_APP_START_DELAY: '__jarabot_credit_app_start_delay',
	KYC_NAME_MISMATCH_ENABLED: '__jarabot_kyc_name_mismatch_enabled',
	REVOLVING_SUMMARY_ENABLED: '__jarabot_revolving_summary_enabled',
	REVOLVING_SUMMARY_DELAY: '__jarabot_revolving_summary_delay',
} as const;

export const JARABOT_DELAYS = {
	SMS_TRIGGER_1: 30_000,
	SMS_TRIGGER_2: 60_000,
	IDENTITY: 30_000,
	CREDIT_APP_START: 20_000,
	REVOLVING_SUMMARY: 10_000,
} as const;

const pendingQueue: QueuedCall[] = [];
let isReady = false;

const isDev = process.env.TARGET_ENV !== 'prod';

function log(method: string, payload?: unknown): void {
	if (isDev) {
		// eslint-disable-next-line no-console
		console.log('[Jarabot]', method, payload ?? '');
	}
}

if (typeof window !== 'undefined') {
	if (window.$jarabot?.hasLoaded) {
		isReady = true;
	} else {
		window.addEventListener(
			'jarabot:ready',
			() => {
				isReady = true;
				pendingQueue.splice(0).forEach((fn) => fn());
			},
			{ once: true }
		);
	}

	if (isDev) {
		(
			[
				'jarabot:ready',
				'jarabot:on-message',
				'jarabot:on-start-conversation',
				'jarabot:custom-action',
				'jarabot:error',
			] as const
		).forEach((eventName) => {
			window.addEventListener(eventName, (e: Event) => {
				log(`incoming:${eventName}`, (e as CustomEvent).detail);
			});
		});
	}
}

function schedule(fn: QueuedCall): void {
	if (isReady) {
		fn();
	} else {
		pendingQueue.push(fn);
	}
}

export const jarabotClient = {
	sendMessage(event: JarabotEvent, detailInfo?: Record<string, unknown>, hidden?: boolean): void {
		log('sendMessage', { event, detailInfo, hidden });
		schedule(() => {
			window.$jarabot?.sendMessage({ message: event, detail_info: detailInfo, hidden });
		});
	},

	setUser(identifier: string, attrs: JarabotUserIdentity): void {
		log('setUser', { identifier, attrs });
		schedule(() => {
			window.$jarabot?.setUser(identifier, attrs as Record<string, unknown>);
		});
	},

	setCustomAttributes(attrs: JarabotContactAttributes): void {
		log('setCustomAttributes', attrs);
		schedule(() => {
			window.$jarabot?.setCustomAttributes(attrs as Record<string, unknown>);
		});
	},

	setConversationCustomAttributes(attrs: Record<string, unknown>): void {
		log('setConversationCustomAttributes', attrs);
		schedule(() => {
			window.$jarabot?.setConversationCustomAttributes(attrs);
		});
	},

	deleteCustomAttribute(key: string): void {
		log('deleteCustomAttribute', key);
		schedule(() => {
			window.$jarabot?.deleteCustomAttribute(key);
		});
	},

	deleteConversationCustomAttribute(key: string): void {
		log('deleteConversationCustomAttribute', key);
		schedule(() => {
			window.$jarabot?.deleteConversationCustomAttribute(key);
		});
	},

	reset(): void {
		log('reset');
		schedule(() => {
			window.$jarabot?.reset();
		});
	},

	toggle(state?: 'open' | 'close'): void {
		log('toggle', state);
		schedule(() => {
			window.$jarabot?.toggle(state);
		});
	},
};
